import React, { useCallback, useContext, useEffect, useState } from 'react'

import ServerSideContext from '../../contexts/ServerSideContext'
import {
  useSuggestionsLazyQuery,
  useGetBucketListItemsIndexQuery,
} from '../../hooks/useQueries'

import StoryCard from '../../components/cards/StoryCard'
import ListingCard from '../../components/cards/ListingCard'
import WithSelectEscape from '../../components/SelectEscape/WithSelectEscape'
import TrackVisibility from '../../components/utils/TrackVisibility'
import PaginatedSlides from '../../components/display/PaginatedSlides'
import { Title } from '../../components/display/PaginatedSlides/shared'

type UseScopedEscapesArgs = {
  canFetchMoreRegional: boolean
  regionalSuggestionsCount?: number
  loadMoreEscapesInMarket: () => any
}

const useScopedEscapes = ({
  canFetchMoreRegional,
  regionalSuggestionsCount,
  loadMoreEscapesInMarket,
}: UseScopedEscapesArgs) => {
  const [hasEnoughRegionalEscapes, setHasEnoughRegionalEscapes] = useState(true)

  useEffect(() => {
    if (canFetchMoreRegional === false && regionalSuggestionsCount < 3) {
      setHasEnoughRegionalEscapes(false)
    }
  }, [canFetchMoreRegional, regionalSuggestionsCount, hasEnoughRegionalEscapes])

  useEffect(() => {
    if (hasEnoughRegionalEscapes === false) {
      loadMoreEscapesInMarket()
    }
  }, [hasEnoughRegionalEscapes])

  return hasEnoughRegionalEscapes
}

type StorySuggestion = GQL_StoryCardFragmentFragment & {
  featuredListing: GQL_StoryFeaturedListingFragmentFragment
}
type ListingSuggestion = GQL_ListingCardFragmentFragment
type Suggestion = StorySuggestion | ListingSuggestion

const SuggestionItem = ({ item }: { item: Suggestion }) => {
  const {
    data: bucketListItemsIndexQueryData,
  } = useGetBucketListItemsIndexQuery()

  const bucketListItemsIndex =
    bucketListItemsIndexQueryData?.currentUser?.bucketListItemsIndex

  // eslint-disable-next-line react/destructuring-assignment
  switch (item.__typename) {
    case 'Story': {
      const { id, featuredListing } = item

      return (
        <WithSelectEscape
          itemId={id}
          itemType="Story"
          bucketListItemsIndex={bucketListItemsIndex}
        >
          <StoryCard story={item} listing={featuredListing} portrait={false} />
        </WithSelectEscape>
      )
    }
    case 'Listing': {
      const { id } = item

      return (
        <WithSelectEscape
          itemId={id}
          itemType="Listing"
          bucketListItemsIndex={bucketListItemsIndex}
        >
          <ListingCard listing={item} />
        </WithSelectEscape>
      )
    }
    default:
      return null
  }
}

type SuggestionsProps = {
  listing: {
    id: string
    state: string
    region_name: string
    region: {
      slug: string
    }
    market: {
      name: string
      code: string
    }
  }
  customTitle?: React.ReactNode
}

const Suggestions = ({ listing, customTitle }: SuggestionsProps) => {
  const serverSide = useContext(ServerSideContext)
  const [visible, setVisible] = useState(false)

  const [
    loadMoreEscapesInRegion,
    {
      called: moreEscapesRegionCalled,
      loading: moreEscapesRegionLoading,
      data: moreEscapesRegionData,
      fetchMore: fetchMoreRegion,
    },
  ] = useSuggestionsLazyQuery({
    variables: {
      listingId: listing.id,
      scope: 'region',
    },
    fetchPolicy: 'cache-and-network',
  })

  const regionalSuggestions = moreEscapesRegionData?.moreEscapes?.edges?.map(
    ({ node }) => node.result
  ) as Suggestion[]

  const regionPageInfo = moreEscapesRegionData?.moreEscapes?.pageInfo

  const canFetchMoreRegional = regionPageInfo?.hasNextPage

  useEffect(() => {
    if (visible) {
      loadMoreEscapesInRegion()
    }
  }, [visible])

  const handleFetchMoreRegional = useCallback(() => {
    if (moreEscapesRegionCalled && !moreEscapesRegionLoading) {
      fetchMoreRegion({
        variables: {
          cursor: regionPageInfo?.endCursor,
        },
      })
    }
  }, [
    fetchMoreRegion,
    moreEscapesRegionCalled,
    moreEscapesRegionLoading,
    regionPageInfo,
  ])

  const [
    loadMoreEscapesInMarket,
    {
      called: moreEscapesMarketCalled,
      loading: moreEscapesMarketLoading,
      data: moreEscapesMarketData,
      fetchMore: fetchMoreMarket,
    },
  ] = useSuggestionsLazyQuery({
    variables: {
      listingId: listing.id,
      scope: 'market',
    },
    fetchPolicy: 'cache-and-network',
  })

  const hasEnoughRegionalEscapes = useScopedEscapes({
    canFetchMoreRegional,
    regionalSuggestionsCount: regionalSuggestions?.length,
    loadMoreEscapesInMarket,
  })

  const marketPageInfo = moreEscapesMarketData?.moreEscapes?.pageInfo

  const canFetchMoreMarket = marketPageInfo?.hasNextPage

  const marketSuggestions = moreEscapesMarketData?.moreEscapes?.edges?.map(
    ({ node }) => node.result
  ) as Suggestion[]

  const handleFetchMoreMarket = useCallback(() => {
    if (moreEscapesMarketCalled && !moreEscapesMarketLoading) {
      fetchMoreMarket({
        variables: {
          cursor: marketPageInfo?.endCursor,
        },
      })
    }
  }, [
    fetchMoreMarket,
    moreEscapesMarketCalled,
    moreEscapesMarketLoading,
    marketPageInfo,
  ])

  if (serverSide) return null

  return (
    <TrackVisibility
      onChange={(isVisible) => {
        if (isVisible) setVisible(true)
      }}
    >
      {hasEnoughRegionalEscapes ? (
        <PaginatedSlides
          customTitle={
            customTitle || (
              <Title>
                More{' '}
                {listing?.state && listing?.region && (
                  <a
                    href={`/${listing.state.toLowerCase()}/${
                      listing.region.slug
                    }`}
                  >
                    {listing?.region_name}
                  </a>
                )}{' '}
                Escapes
              </Title>
            )
          }
          called={moreEscapesRegionCalled}
          loading={moreEscapesRegionLoading}
          canFetchMore={canFetchMoreRegional}
          onFetchMore={handleFetchMoreRegional}
          items={regionalSuggestions}
          ItemComponent={SuggestionItem}
          minHeight=""
        />
      ) : (
        <PaginatedSlides
          customTitle={
            customTitle || <Title>More {listing?.market?.name} Escapes</Title>
          }
          called={moreEscapesMarketCalled}
          loading={moreEscapesMarketLoading}
          canFetchMore={canFetchMoreMarket}
          onFetchMore={handleFetchMoreMarket}
          items={marketSuggestions}
          ItemComponent={SuggestionItem}
          minHeight=""
        />
      )}
    </TrackVisibility>
  )
}

export default Suggestions
